var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from 'react';
import AdapterMoment from '@mui/lab/AdapterMoment';
import { LocalizationProvider } from '@mui/lab';
import { createTheme, StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
export * from './styleOverridesTypes';
var colors = {
    whiteColor: '#FFFFFF',
    darkColor: '#061220',
    lightColor: '#FDFDFD',
    errorColor: '#D5282B',
    successColor: '#4F9900',
    warningColor: '#F7A600',
    infoColor: '#4C629E',
    secondaryColor100: '#F9F9FA',
    secondaryColor200: '#F3F4F5',
    secondaryColor300: '#E7E9EC',
    secondaryColor400: '#CED3D9',
    secondaryColor500: '#B6BDC6',
    secondaryColor600: '#9EA7B3',
    secondaryColor700: '#8691A0',
    secondaryColor800: '#6D7B8C',
    secondaryColor900: '#556579',
    invertedHoverColor: '#FFFFFF33'
};
var createColor = function (color) { return createTheme().palette.augmentColor({ color: { main: color } }); };
var AccurityMuiThemeProvider = function (_a) {
    var primaryColor = _a.primaryColor, children = _a.children;
    var theme = createTheme({
        breakpoints: {
            values: {
                xs: 0,
                sm: 0,
                md: 0,
                lg: 0,
                xl: 0,
            }
        },
        palette: {
            primary: {
                main: primaryColor
            },
            secondary: {
                main: colors.darkColor,
            },
            error: {
                main: colors.errorColor
            },
            warning: {
                main: colors.warningColor
            },
            info: {
                main: colors.infoColor
            },
            success: {
                main: colors.successColor
            },
            /**
             * For custom textColor, the color should be added below AND to the 'TypeText' interface in 'styleOverridesTypes.ts'
             * Primary, secondary, error, warning and success color will also work, although using them directly is not recomanded
             *
             * These collors currently CANNOT be added to the autocomplete (MUI Bug)
             */
            text: {
                primary: colors.darkColor,
                secondary: colors.secondaryColor700,
                inverted: colors.lightColor,
                disabled: colors.secondaryColor500
            },
            /**
             * For custom backgroundColor, the color needs to be added below AND to the 'TypeBackground' interface in 'styleOverridesTypes.ts'
             * These collors currently CANNOT be added to the autocomplete (MUI Bug)
             */
            background: {
                default: colors.secondaryColor300,
                paper: colors.whiteColor,
                inverted: colors.darkColor,
                hover: colors.secondaryColor200,
                invertedHover: colors.invertedHoverColor,
            },
            /**
             * For custom Color (that is NOT either background or text color), the color needs to be added below
             * AND to both the 'Palette' and 'PaletteOptions' interfaces in 'styleOverridesTypes.ts'
             */
            secondary300: createColor(colors.secondaryColor300),
            secondary700: createColor(colors.secondaryColor700),
            white: createColor(colors.whiteColor),
            light: createColor(colors.lightColor),
            dark: createColor(colors.darkColor),
            disabled: createColor(colors.secondaryColor500),
            divider: colors.secondaryColor300
        },
        typography: {
            fontFamily: 'inherit',
            fontWeightLight: 200,
            fontWeightRegular: 300,
            fontWeightMedium: 400,
            fontWeightBold: 700,
            fontSize: 14,
            h2: {
                fontFamily: 'inherit',
                fontSize: '1.429rem',
                fontWeight: 700
            },
            h3: {
                fontFamily: 'inherit',
                fontSize: '1.357rem',
                fontWeight: 400
            },
            h5: {
                fontFamily: 'inherit',
                fontSize: '1rem',
                fontWeight: 500,
                color: colors.secondaryColor700
            },
            button: {
                fontFamily: 'inherit',
                fontSize: '1rem',
                fontWeight: 400
            },
            body1: {
                fontFamily: 'inherit',
                fontSize: '1rem',
                fontWeight: 300
            },
            note: {
                fontFamily: 'inherit',
                fontSize: '0.8rem',
                fontWeight: 300,
                color: colors.secondaryColor700,
                backgroundColor: colors.whiteColor
            }
        },
        /**
         * In defaultProps, we can only use custom colors if the color is present in the 'ColorOverrides' interface AND
         * if the components 'PropsColorOverrides' interface is extended with it (see styleOverridesTypes.ts)
         *
         * Not every component has PropsColorOverrides!
         *
         * In styleOverrides, we have to use the color code directly
         */
        components: {
            MuiCardHeader: {
                defaultProps: {
                    titleTypographyProps: { variant: 'h2' }
                }
            },
            MuiAppBar: {
                defaultProps: {
                    elevation: 0,
                    color: 'white',
                }
            },
            MuiFormControl: {
                defaultProps: {
                    variant: 'standard',
                }
            },
            MuiSelect: {
                defaultProps: {
                    variant: 'standard',
                }
            },
            MuiTextField: {
                defaultProps: {
                    variant: 'standard',
                },
                styleOverrides: {
                    root: {
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: colors.secondaryColor700
                            },
                            '&:hover fieldset': {
                                borderColor: colors.secondaryColor700,
                                borderWidth: '2px'
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: primaryColor
                            },
                            '&.Mui-disabled fieldset': {
                                borderWidth: '1px'
                            }
                        }
                    }
                }
            },
            MuiChip: {
                defaultProps: {
                    color: 'secondary300'
                },
                styleOverrides: {
                    root: {
                        maxWidth: '100%'
                    },
                    deleteIcon: {
                        color: colors.secondaryColor700
                    }
                }
            },
            MuiIconButton: {
                defaultProps: {
                    color: 'secondary700'
                }
            },
            MuiInput: {
                styleOverrides: {
                    underline: {
                        '&:before': {
                            borderColor: colors.secondaryColor700,
                            transition: 'all 300ms'
                        },
                        '&:hover:not(.Mui-disabled):before': {
                            borderColor: colors.secondaryColor700
                        },
                        '&.Mui-disabled:before': {
                            borderWidth: '2px'
                        }
                    }
                }
            },
            MuiTableCell: {
                styleOverrides: {
                    root: (function (_a) {
                        var ownerState = _a.ownerState;
                        return (__assign({}, (ownerState.stickyHeader === true && { backgroundColor: colors.whiteColor })));
                    })
                },
            },
            MuiPaper: {
                defaultProps: {
                    elevation: 0,
                }
            },
            MuiAutocomplete: {
                defaultProps: {
                    componentsProps: { paper: { elevation: 1 } }
                }
            },
        }
    });
    return (React.createElement(StyledEngineProvider, { injectFirst: true },
        React.createElement(ThemeProvider, { theme: theme },
            React.createElement(LocalizationProvider, { dateAdapter: AdapterMoment }, children))));
};
export default AccurityMuiThemeProvider;
//# sourceMappingURL=AccurityMuiThemeProvider.js.map